import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import DocumentCreation from '../../components/documents/DocumentCreation';
import DocumentPreview from '../../components/documents/DocumentPreview';
import { 
  DocumentTextIcon,
  PlusIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppData } from '../../contexts/AppDataContext';
import { useDocuments } from '../../contexts/DocumentsContext';
import toast from 'react-hot-toast';



const DocumentOverview = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { 
    customers, 
    products, 
    suppliers, 
    fetchAllData,
    getCustomers,
    getSuppliers 
  } = useAppData();
  
  const { 
    documents, 
    stats, 
    loading, 
    fetchDocuments, 
    updateDocumentStatus,
    deleteDocument 
  } = useDocuments();
  
  const [searchTerm, setSearchTerm] = useState('');
  
  // Load fresh data when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchAllData();
        await fetchDocuments();
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadData();
  }, []); // Empty dependency array to prevent infinite loops

  // Debug effect to log when data is loaded
  useEffect(() => {
    console.log('Documents page data status:', {
      customersLoaded: customers?.length || 0,
      suppliersLoaded: suppliers?.length || 0,
      documentsLoaded: documents?.length || 0,
      sampleCustomer: customers?.[0],
      sampleDocument: documents?.[0]
    });
  }, [customers, suppliers, documents]);



  // Use real documents from backend
  const allDocuments = documents;

  const filteredDocuments = allDocuments && allDocuments.length > 0 ? allDocuments.filter(doc => {
    const matchesSearch = !searchTerm || 
      doc.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  }) : [];

  const totalDocuments = stats.total || (allDocuments ? allDocuments.length : 0);
  const totalAmount = stats.totalAmount || (allDocuments ? allDocuments.reduce((sum, doc) => sum + (doc.total || 0), 0) : 0);
  const pendingDocuments = stats.byStatus?.pending || (allDocuments ? allDocuments.filter(doc => doc.status === 'pending' || doc.status === 'draft').length : 0);

  const handleDeleteDocument = async (docId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      await deleteDocument(docId);
    }
  };

  const handleStatusChange = async (docId, newStatus) => {
    await updateDocumentStatus(docId, newStatus);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'text-green-400 bg-green-400/10';
      case 'confirmed':
      case 'delivered':
        return 'text-blue-400 bg-blue-400/10';
      case 'pending':
        return 'text-yellow-400 bg-yellow-400/10';
      case 'draft':
        return 'text-gray-400 bg-gray-400/10';
      case 'cancelled':
        return 'text-red-400 bg-red-400/10';
      default:
        return 'text-gray-400 bg-gray-400/10';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return 'Brouillon';
      case 'pending': return 'En attente';
      case 'confirmed': return 'Confirmé';
      case 'delivered': return 'Livré';
      case 'paid': return 'Payé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const getDocumentTypeText = (type) => {
    switch (type) {
      case 'invoice': return 'Facture';
      case 'delivery-note': return 'Bon de livraison';
      case 'purchase-order': return 'Bon de commande';
      case 'reception-note': return 'Bon de réception';
      default: return type;
    }
  };

  // Function to resolve contact name from document
  const resolveContactName = (document) => {
    console.log('🔍 Resolving contact for document:', {
      docId: document.id,
      storedContactName: document.contactName,
      clientId: document.clientId,
      supplierId: document.supplierId,
      customersAvailable: customers?.length || 0,
      suppliersAvailable: suppliers?.length || 0
    });

    // First try the stored contactName
    if (document.contactName && document.contactName !== 'Contact inconnu') {
      console.log('✅ Using stored contact name:', document.contactName);
      return document.contactName;
    }

    // If no contactName or it's unknown, try to resolve from current data
    let contact = null;
    if (document.clientId && customers?.length > 0) {
      contact = customers.find(c => c.id === document.clientId);
      console.log('🔍 Client lookup:', { 
        clientId: document.clientId, 
        found: !!contact, 
        contact: contact ? { id: contact.id, name: contact.name, firstName: contact.firstName, lastName: contact.lastName } : null 
      });
    } else if (document.supplierId && suppliers?.length > 0) {
      contact = suppliers.find(s => s.id === document.supplierId);
      console.log('🔍 Supplier lookup:', { 
        supplierId: document.supplierId, 
        found: !!contact, 
        contact: contact ? { id: contact.id, name: contact.name, firstName: contact.firstName, lastName: contact.lastName } : null 
      });
    }

    if (contact) {
      const resolvedName = contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Contact sans nom';
      console.log('✅ Resolved contact name:', resolvedName);
      return resolvedName;
    }

    console.log('❌ No contact found, returning Contact inconnu');
    return 'Contact inconnu';
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Documents Commerciaux</h1>
            <p className="text-white/60">Gérez tous vos documents commerciaux</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/documents/new')}
            className="flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-medium transition-all"
          >
            <PlusIcon className="h-5 w-5" />
            Nouveau document
          </motion.button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">Total documents</p>
                <p className="text-2xl font-bold text-white">{totalDocuments}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <DocumentTextIcon className="h-6 w-6 text-blue-400" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">Montant total</p>
                <p className="text-2xl font-bold text-white">{totalAmount.toLocaleString()} DA</p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-xl">
                <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">En attente</p>
                <p className="text-2xl font-bold text-white">{pendingDocuments}</p>
              </div>
              <div className="p-3 bg-yellow-500/20 rounded-xl">
                <CalendarIcon className="h-6 w-6 text-yellow-400" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
              <input
                type="text"
                placeholder="Rechercher un document..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
            </div>
          </div>
        </div>

        {/* Documents List */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 overflow-hidden">
          {loading && (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-white/60">Chargement des documents...</p>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-white/80">Type</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-white/80">Numéro</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-white/80">Client/Fournisseur</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-white/80">Montant</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-white/80">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-white/80">Statut</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-white/80">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.length > 0 ? (
                  filteredDocuments.map((document, index) => (
                    <motion.tr
                      key={document.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border-t border-white/10 hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="text-white font-medium">
                          {getDocumentTypeText(document.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-white font-mono">{document.number}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <UserIcon className="h-4 w-4 text-white/50" />
                          <span className="text-white">{resolveContactName(document)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-white font-medium">
                          {(document.total || 0).toLocaleString()} DA
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-white/70">{document.date}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(document.status)}`}>
                          {getStatusText(document.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/documents/${document.id}`)}
                            className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                            title="Voir"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/documents/${document.id}/edit`)}
                            className="p-2 text-yellow-400 hover:bg-yellow-400/10 rounded-lg transition-colors"
                            title="Modifier"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(document.id)}
                            className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <DocumentTextIcon className="h-12 w-12 mx-auto text-white/30 mb-4" />
                      <p className="text-white/60 text-lg mb-2">Aucun document trouvé</p>
                      <p className="text-white/40">Commencez par créer votre premier document</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchDocument, documents } = useDocuments();
  const { customers, suppliers } = useAppData();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check for refresh parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const shouldRefresh = urlParams.get('refresh') === 'true';

  // Define getDocumentTypeText function first
  const getDocumentTypeText = (type) => {
    const types = {
      'invoice': 'Facture',
      'quote': 'Devis',
      'delivery-note': 'Bon de livraison',
      'purchase-order': 'Bon de commande',
      'reception-note': 'Bon de réception'
    };
    return types[type] || type;
  };

  // Function to resolve contact name from document
  const resolveContactName = (document) => {
    // First try the stored contactName
    if (document.contactName && document.contactName !== 'Contact inconnu') {
      return document.contactName;
    }

    // If no contactName or it's unknown, try to resolve from current data
    let contact = null;
    if (document.clientId) {
      contact = customers?.find(c => c.id === document.clientId);
    } else if (document.supplierId) {
      contact = suppliers?.find(s => s.id === document.supplierId);
    }

    if (contact) {
      return contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Contact sans nom';
    }

    return 'Contact inconnu';
  };

  const refreshDocument = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const fetchedDoc = await fetchDocument(id);
      if (fetchedDoc) {
        setDocument(fetchedDoc);
        toast.success('Document mis à jour');
      }
    } catch (error) {
      console.error('Error refreshing document:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadDocument = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // First try to find the document in the local cache
        const localDoc = documents.find(doc => doc.id === id);
        
        // If we have a local document, use it immediately to prevent the "Document non trouvé" error
        if (localDoc) {
          setDocument(localDoc);
          setLoading(false);
        }
        
        // Always fetch fresh data from API to ensure latest updates
        const fetchedDoc = await fetchDocument(id);
        if (fetchedDoc) {
          setDocument(fetchedDoc);
          
          // Clear refresh parameter from URL if present
          if (shouldRefresh) {
            window.history.replaceState(null, '', `/documents/${id}`);
          }
        } else if (!localDoc) {
          // Only show error if we don't have a local document
          setError('Document non trouvé');
        }
      } catch (err) {
        console.error('Error loading document:', err);
        // If we don't have a local document already set, try to find it again
        if (!document) {
          const existingDoc = documents.find(doc => doc.id === id);
          if (existingDoc) {
            setDocument(existingDoc);
          } else {
            setError('Erreur lors du chargement du document');
          }
        }
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [id, fetchDocument, shouldRefresh]);

  // Separate effect to listen for updates to the documents array
  useEffect(() => {
    if (documents.length > 0 && id) {
      const updatedDoc = documents.find(doc => doc.id === id);
      if (updatedDoc && document && JSON.stringify(updatedDoc) !== JSON.stringify(document)) {
        // Document has been updated, refresh the view
        console.log('Document updated, refreshing view:', updatedDoc);
        setDocument(updatedDoc);
        // Clear any error if we found the document
        setError(null);
      } else if (updatedDoc && !document) {
        // We found the document in the cache but it wasn't set yet
        console.log('Document found in cache, setting view:', updatedDoc);
        setDocument(updatedDoc);
        setError(null);
      }
    }
  }, [documents, id, document]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-white/60">Chargement du document...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Erreur</h1>
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/documents')}
            className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
          >
            Retour aux documents
          </button>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
  <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Document non trouvé</h1>
          <button
            onClick={() => navigate('/documents')}
            className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
          >
            Retour aux documents
          </button>
        </div>
  </div>
);
  }

  // Convert document data to formData format that DocumentPreview expects
  const formData = {
    number: document.number,
    date: document.date,
    dueDate: document.dueDate || '',
    clientId: document.clientId || '',
    supplierId: document.supplierId || '',
    items: document.items || [],
    subtotal: document.subtotal || 0,
    taxRate: document.taxRate || 19,
    taxAmount: document.taxAmount || 0,
    total: document.total || 0,
    notes: document.notes || '',
    paymentMethod: document.paymentMethod || '',
    salesPerson: document.salesPerson || '',
    deliveryDate: document.deliveryDate || '',
    deliveryAddress: document.deliveryAddress || '',
    conditions: document.conditions || '',
    validUntil: document.validUntil || '',
    estimatedDelivery: document.estimatedDelivery || '',
    paymentTerms: document.paymentTerms || '',
    issuedBy: document.issuedBy || '',
    approvedBy: document.approvedBy || '',
    contactName: resolveContactName(document),
    contactEmail: document.contactEmail,
    contactPhone: document.contactPhone,
    contactAddress: document.contactAddress
  };

  // Document type object that DocumentPreview expects
  const documentType = {
    id: document.type,
    title: getDocumentTypeText(document.type)
  };

  return (
    <DocumentPreview
      documentType={documentType}
      formData={formData}
      onBack={() => navigate('/documents')}
      onSave={() => {
        // No save action needed in view mode, just go back
        navigate('/documents');
      }}
      viewMode={true}
      onRefresh={refreshDocument}
    />
  );
};

const EditDocument = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchDocument, updateDocument, documents } = useDocuments();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDocument = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // First try to find in existing documents
        const existingDoc = documents.find(doc => doc.id === id);
        if (existingDoc) {
          setDocument(existingDoc);
          setLoading(false);
          return;
        }
        
        // If not found, fetch from API
        const fetchedDoc = await fetchDocument(id);
        if (fetchedDoc) {
          setDocument(fetchedDoc);
        } else {
          setError('Document non trouvé');
        }
      } catch (err) {
        console.error('Error loading document:', err);
        setError('Erreur lors du chargement du document');
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [id, fetchDocument, documents]);

  const handleSave = async (updatedData) => {
    try {
      const success = await updateDocument(id, updatedData);
      if (success) {
        // Navigate back to the document view with refresh parameter
        navigate(`/documents/${id}?refresh=true`);
      }
    } catch (error) {
      console.error('Error updating document:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-white/60">Chargement du document...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Erreur</h1>
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/documents')}
            className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
          >
            Retour aux documents
          </button>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
  <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Document non trouvé</h1>
          <button
            onClick={() => navigate('/documents')}
            className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
          >
            Retour aux documents
          </button>
        </div>
  </div>
);
  }

  return (
    <DocumentCreation
      editMode={true}
      documentToEdit={document}
      onSave={handleSave}
      onCancel={() => navigate('/documents')}
    />
  );
};

const DocumentsPage = () => {
  return (
    <Routes>
      <Route path="/" element={<DocumentOverview />} />
      <Route path="/new" element={<DocumentCreation />} />
      <Route path="/:id" element={<DocumentDetail />} />
      <Route path="/:id/edit" element={<EditDocument />} />
    </Routes>
  );
};

export default DocumentsPage;